import { createAlbum } from '@/app/services/albums/albums';
import { getAllRooms } from '@/app/services/rooms/rooms';
import * as Actions from '@/app/store/actions';
import ModalBase from '@/app/customComponents/modals/ModalBase';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const CreateAlbumModal = ({ open, setOpen, onAlbumCreated, initialMediaIds = [] }) => {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [roomId, setRoomId] = useState('');
	const [rooms, setRooms] = useState([]);
	const [roomsLoading, setRoomsLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		if (open) {
			const fetchRooms = async () => {
				try {
					setRoomsLoading(true);
					const res = await getAllRooms();
					setRooms(res.data);
				} catch (error) {
					dispatch(Actions.showMessage({ message: 'Failed to fetch rooms', variant: 'error' }));
				} finally {
					setRoomsLoading(false);
				}
			};
			fetchRooms();
		}
	}, [open]);

	const handleSubmit = async () => {
		if (!name.trim()) {
			dispatch(Actions.showMessage({ message: 'Album name is required', variant: 'error' }));
			return;
		}

		setSaving(true);
		try {
			const payload = {
				name: name.trim(),
				description: description.trim() || undefined,
				room_id: roomId || undefined,
				media_ids: initialMediaIds.length > 0 ? initialMediaIds : undefined,
			};

			const res = await createAlbum(payload);
			dispatch(Actions.showMessage({ message: 'Album created successfully', variant: 'success' }));
			setName('');
			setDescription('');
			setRoomId('');
			setOpen(false);
			if (onAlbumCreated) onAlbumCreated(res.data.data || res.data);
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to create album';
			dispatch(Actions.showMessage({ message, variant: 'error' }));
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		setName('');
		setDescription('');
		setRoomId('');
	};

	return (
		<ModalBase maxWidth={480} onClose={handleClose} open={open} setOpen={setOpen} showClose={false}>
			<h2 className="text-20 font-bold text-gray-900 mb-20">Create New Album</h2>
			<div className="create-album-form">
				<div className="form-group">
					<label htmlFor="album-name">Album Name *</label>
					<input
						autoFocus
						id="album-name"
						maxLength={255}
						onChange={(e) => setName(e.target.value)}
						placeholder="e.g. Christmas Party 2025"
						type="text"
						value={name}
					/>
				</div>
				<div className="form-group">
					<label htmlFor="album-description">Description</label>
					<textarea
						id="album-description"
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Optional description for this album"
						rows={3}
						value={description}
					/>
				</div>
				<div className="form-group">
					<FormControl fullWidth variant="outlined" size="small">
						<InputLabel>Room (optional)</InputLabel>
						<Select
							label="Room (optional)"
							onChange={(e) => setRoomId(e.target.value)}
							value={roomId}
						>
							<MenuItem value="">
								<em>No room - available to all</em>
							</MenuItem>
							{roomsLoading ? (
								<MenuItem disabled>Loading rooms...</MenuItem>
							) : (
								rooms.map((room) => (
									<MenuItem key={room.id} value={room.id}>
										{room.name}
									</MenuItem>
								))
							)}
						</Select>
					</FormControl>
				</div>
				{initialMediaIds.length > 0 && (
					<p className="text-14 text-gray-500 mb-16">
						{initialMediaIds.length} item{initialMediaIds.length !== 1 ? 's' : ''} will be added to this
						album.
					</p>
				)}
			</div>
			<div className="flex justify-end gap-12 mt-20">
				<CustomButton
					onClick={() => {
						handleClose();
						setOpen(false);
					}}
					variant="secondary"
				>
					Cancel
				</CustomButton>
				<CustomButton disabled={saving || !name.trim()} onClick={handleSubmit}>
					{saving ? <CircularProgress size={20} /> : 'Create Album'}
				</CustomButton>
			</div>
		</ModalBase>
	);
};

export default CreateAlbumModal;
